/* ── Self-hosted fonts ─────────────────────────────────────────────────── */
@font-face {
  font-family: 'Outfit';
  font-style: normal;
  font-weight: 300 700;
  font-display: swap;
  src: url('/fonts/Outfit.woff2') format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA,
    U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193,
    U+2212, U+2215, U+FEFF, U+FFFD;
}

@font-face {
  font-family: 'JetBrains Mono';
  font-style: normal;
  font-weight: 400 700;
  font-display: swap;
  src: url('/fonts/JetBrainsMono.woff2') format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA,
    U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193,
    U+2212, U+2215, U+FEFF, U+FFFD;
}

/* ── Design tokens (CSS custom properties) ────────────────────────────── */
:root {
  /* Brand */
  --teal-300: #5FD9CF;
  --teal-500: #16B7AB;
  --teal-600: #0F9D92;

  /* Colors */
  --color-primary:        #0F9D92;
  --color-primary-hover:  #16B7AB;
  --color-primary-light:  #F4FAF9;
  --color-primary-subtle: #5FD9CF;
  --color-surface-tinted: #F4FAF9;
  --color-surface-mist:   #F6FAF9;
  --color-bg:             #ECEEF0;
  --color-surface:        #FFFFFF;
  --color-border:         #EEF0F1;
  --color-text:           #0A1413;
  --color-text-secondary: #6B7180;
  --color-text-muted:     #9AA0AD;

  /* Fonts */
  --font-sans: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  --font-mono: 'JetBrains Mono', 'Fira Code', monospace;

  /* Layout */
  --sidebar-width:  220px;
  --topbar-height:  56px;

  /* Shadows */
  --shadow-xs: 0 1px 2px rgba(10,20,19,0.04);
  --shadow-sm: 0 1px 4px rgba(10,20,19,0.06);
  --shadow-md: 0 2px 8px rgba(10,20,19,0.08);
  --shadow-lg: 0 4px 16px rgba(10,20,19,0.10);
  --shadow-xl: 0 8px 24px rgba(10,20,19,0.12);

  /* Focus */
  --focus-ring: 0 0 0 3px rgba(15,157,146,0.22);

  /* Transitions */
  --transition-fast: 120ms ease;
  --transition-base: 170ms ease;
  --transition-slow: 280ms ease;
}

/* ── Test-mode theme override (driven by data-theme="test" on <html>) ──── */
:root[data-theme="test"] {
  --teal-300: #FDBA74;
  --teal-500: #EA580C;
  --teal-600: #C2410C;

  --color-primary:        #C2410C;
  --color-primary-hover:  #EA580C;
  --color-primary-light:  #FFF7ED;
  --color-primary-subtle: #FDBA74;
  --color-surface-tinted: #FFF7ED;
  --color-surface-mist:   #FBF4EE;
  --color-bg:             #FBF4EE;
  --color-border:         #F1E7DD;

  --focus-ring: 0 0 0 3px rgba(234,88,12,0.22);
}

/* ── Reset ─────────────────────────────────────────────────────────────── */
*, *::before, *::after {
  box-sizing: border-box;
}

/* ── Base ──────────────────────────────────────────────────────────────── */
/* Phones/tablets: the page itself never pans sideways; wide content scrolls inside its own container. */
html, body { overflow-x: hidden; overscroll-behavior-x: none; max-width: 100%; }
/* App frame fills the *visible* screen. 100vh on iOS Safari is taller than the area above the toolbar,
   so the whole page scrolled as well as the content pane — two scrollers fighting. dvh fixes that. */
.app-shell { height: 100vh; height: 100dvh; width: 100%; max-width: 100vw; }

body {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-text);
  background: var(--color-bg);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

code, pre, kbd, samp {
  font-family: var(--font-mono);
}

/* ── Scrollbar (thin, teal thumb) ─────────────────────────────────────── */
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 9999px; }
::-webkit-scrollbar-thumb:hover { background: #9AA0AD; }

/* ── Focus ring (keyboard nav) ─────────────────────────────────────────── */
:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* ── Animations ─────────────────────────────────────────────────────────── */
/* Referenced from inline styles: Button/GlobalSearch spinners, Modal enter,
   ProgressBar indeterminate. */
@keyframes docm-spin {
  to { transform: rotate(360deg); }
}

@keyframes docm-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

@keyframes docm-slide-in-up {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Sweeps a partial-width bar across the track for work with no known duration. */
@keyframes docm-indeterminate {
  from { transform: translateX(-100%); }
  to   { transform: translateX(300%); }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* ── Link defaults ──────────────────────────────────────────────────────── */
a {
  color: var(--color-primary);
  text-decoration: none;
}
a:hover {
  color: var(--color-primary-hover);
  text-decoration: underline;
}

/* Rich notification bodies: keep embedded media within the bell/modal width. */
.notif-body img, .notif-body video, .notif-body iframe { max-width: 100%; height: auto; border-radius: 6px; }
.notif-body a { color: var(--color-primary); }
